// src/utils/growthCurve.js
// Agregação de curva de crescimento sigmoidal a partir de N experimentos tratados
// como réplicas. A fonte é o ChartResponse do `POST /grafico` (série temporal
// bruta de uma métrica espectral). Aqui reduzimos cada experimento a um sinal
// escalar por instante, alinhamos as réplicas numa grade de tempo comum e
// derivamos média ± desvio-padrão, limiar de detecção, TTD e as fases
// (lag / exponencial / estacionária).
//
// Nada aqui depende de React/ECharts — funções puras, no estilo de sweepFeatures.js.
// O eixo Y é o SINAL medido do sensor (unidade arbitrária), não UFC absoluto.

/** Canal representativo padrão do crescimento (mesmo default do BasicChartModal). */
export const DEFAULT_CHANNEL = 'F2';

/**
 * basic count = RAW / (tint_ms × gain). Mesma fórmula do SpectralBarChart —
 * normaliza a contagem crua pelo tempo de integração e ganho, tornando réplicas
 * medidas com configs diferentes comparáveis. Retorna 0 se faltar tint/gain.
 */
export function toBasicCount(raw, tintMs, gain) {
    if (raw == null || !tintMs || !gain) return 0;
    return raw / (tintMs * gain);
}

// ---------------------------------------------------------------------------
// 1. ChartResponse -> sinal escalar por instante (uma réplica)
// ---------------------------------------------------------------------------

/**
 * Extrai o sinal de um canal de um ChartResponse e converte o eixo do tempo
 * para HORAS DECORRIDAS do próprio início do experimento (t0 = primeiro label).
 *
 * @param {object} chartResponse  – `{ labels:[unixSeg], series:{grupo:[{name,data[]}]} }`
 * @param {string} channel        – ex.: 'F2', 'CLEAR' (case-insensitive)
 * @returns {{tHours:number, value:number}[]} pontos ordenados, sem nulos
 */
export function toReplicaSignal(chartResponse, channel = DEFAULT_CHANNEL, convert) {
    const labels = chartResponse?.labels;
    const series = chartResponse?.series;
    if (!Array.isArray(labels) || labels.length === 0 || !series) return [];

    const wanted = String(channel).toUpperCase();
    let data = null;
    for (const group of Object.keys(series)) {
        const list = series[group] || [];
        const match = list.find((s) => {
            const name = String(s?.name || '').toUpperCase();
            return name === wanted || name.endsWith(`.${wanted}`);
        });
        if (match) {
            data = match.data;
            break;
        }
    }
    if (!Array.isArray(data)) return [];

    const t0 = labels[0];
    const out = [];
    for (let i = 0; i < labels.length; i++) {
        const v = data[i];
        if (v === null || v === undefined || !Number.isFinite(Number(v))) continue;
        const value = convert ? convert(Number(v)) : Number(v);
        if (!Number.isFinite(value)) continue;
        out.push({ tHours: (labels[i] - t0) / 3600, value });
    }
    out.sort((a, b) => a.tHours - b.tHours);
    return out;
}

/** Lista dos canais disponíveis num ChartResponse (sufixos após o ponto). */
export function availableChannels(chartResponse) {
    const series = chartResponse?.series;
    if (!series) return [];
    const set = new Set();
    for (const group of Object.keys(series)) {
        for (const s of series[group] || []) {
            const parts = String(s?.name || '').split('.');
            const ch = parts[parts.length - 1].toUpperCase();
            if (ch) set.add(ch);
        }
    }
    return [...set];
}

/**
 * Média móvel CENTRADA (simétrica), janela de `windowMin` minutos: cada ponto vira a
 * média dos valores em [t - W/2, t + W/2] (janela truncada nas bordas). USO EXCLUSIVO
 * DE EXIBIÇÃO — NÃO alimenta o TTD nem as fases (esses seguem no sinal cru, fiéis ao
 * artigo). Preserva os tHours originais.
 */
export function centeredMovingAverage(signal, windowMin = 30) {
    const n = Array.isArray(signal) ? signal.length : 0;
    if (n === 0 || !(windowMin > 0)) return signal || [];
    const halfH = windowMin / 2 / 60;
    const out = new Array(n);
    let lo = 0;
    let hi = 0;
    let sum = 0;
    let count = 0;
    for (let i = 0; i < n; i++) {
        const t = signal[i].tHours;
        const right = t + halfH;
        const left = t - halfH;
        while (hi < n && signal[hi].tHours <= right) {
            sum += signal[hi].value;
            count++;
            hi++;
        }
        while (lo < n && signal[lo].tHours < left) {
            sum -= signal[lo].value;
            count--;
            lo++;
        }
        out[i] = { tHours: t, value: count > 0 ? sum / count : signal[i].value };
    }
    return out;
}

// ---------------------------------------------------------------------------
// 2. Interpolação / grade de tempo comum
// ---------------------------------------------------------------------------

/** Interpolação linear de um sinal ordenado em t; null fora do intervalo. */
function interpAt(points, t) {
    const n = points.length;
    if (n === 0) return null;
    if (t < points[0].tHours || t > points[n - 1].tHours) return null;
    // busca binária do intervalo [lo, lo+1] que contém t
    let lo = 0;
    let hi = n - 1;
    while (hi - lo > 1) {
        const mid = (lo + hi) >> 1;
        if (points[mid].tHours <= t) lo = mid;
        else hi = mid;
    }
    const a = points[lo];
    const b = points[hi];
    if (b.tHours === a.tHours) return a.value;
    const frac = (t - a.tHours) / (b.tHours - a.tHours);
    return a.value + frac * (b.value - a.value);
}

/** Mediana dos dt consecutivos de um sinal (para escolher o passo da grade). */
function medianDt(points) {
    if (points.length < 2) return null;
    const dts = [];
    for (let i = 1; i < points.length; i++) {
        const dt = points[i].tHours - points[i - 1].tHours;
        if (dt > 0) dts.push(dt);
    }
    if (!dts.length) return null;
    dts.sort((a, b) => a - b);
    return dts[Math.floor(dts.length / 2)];
}

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

/** Passo de grade automático: mediana de dt da réplica mais densa, limitado. */
export function autoGridStep(replicas) {
    let best = null;
    for (const r of replicas) {
        const dt = medianDt(r);
        if (dt != null && (best == null || dt < best)) best = dt;
    }
    if (best == null) return 0.1;
    return clamp(best, 0.02, 0.25);
}

// ---------------------------------------------------------------------------
// 3. Alinhar réplicas -> média ± desvio-padrão numa grade comum
// ---------------------------------------------------------------------------

/**
 * Alinha as réplicas numa grade de tempo comum e agrega.
 *
 * @param {Array<{tHours,value}[]>} replicas
 * @param {object} [opts]
 * @param {number} [opts.gridStep]  – passo em horas (default: automático)
 * @returns {{
 *   grid:number[], mean:(number|null)[], sd:(number|null)[],
 *   counts:number[], aligned:(number|null)[][], gridStep:number
 * }}
 *   `aligned[j]` = a réplica j reamostrada na grade (null fora do intervalo dela).
 */
export function alignAndAggregate(replicas, opts = {}) {
    const valid = (replicas || []).filter((r) => Array.isArray(r) && r.length >= 2);
    if (valid.length === 0) {
        return { grid: [], mean: [], sd: [], counts: [], aligned: [], gridStep: 0 };
    }

    const gridStep = opts.gridStep || autoGridStep(valid);
    const tMax = Math.max(...valid.map((r) => r[r.length - 1].tHours));

    const grid = [];
    for (let t = 0; t <= tMax + 1e-9; t += gridStep) grid.push(Number(t.toFixed(6)));

    const aligned = valid.map((r) => grid.map((t) => interpAt(r, t)));

    const mean = new Array(grid.length).fill(null);
    const sd = new Array(grid.length).fill(null);
    const counts = new Array(grid.length).fill(0);

    for (let i = 0; i < grid.length; i++) {
        const vals = [];
        for (let j = 0; j < aligned.length; j++) {
            const v = aligned[j][i];
            if (v !== null && Number.isFinite(v)) vals.push(v);
        }
        counts[i] = vals.length;
        if (vals.length === 0) continue;
        const m = vals.reduce((a, b) => a + b, 0) / vals.length;
        mean[i] = m;
        if (vals.length >= 2) {
            const variance =
                vals.reduce((a, b) => a + (b - m) ** 2, 0) / (vals.length - 1);
            sd[i] = Math.sqrt(variance);
        } else {
            sd[i] = 0;
        }
    }

    return { grid, mean, sd, counts, aligned, gridStep };
}

// ---------------------------------------------------------------------------
// 4. Limiar de detecção + TTD (Definição 1 do artigo, robusto à direção)
// ---------------------------------------------------------------------------

/** Mediana de um array (ignora não-finitos). */
function median(arr) {
    const a = (arr || []).filter((v) => Number.isFinite(v)).sort((x, y) => x - y);
    if (!a.length) return null;
    const mid = a.length >> 1;
    return a.length % 2 ? a[mid] : (a[mid - 1] + a[mid]) / 2;
}

/** Índices onde a série está definida. */
function definedIdx(mean) {
    const idx = [];
    for (let i = 0; i < mean.length; i++) {
        if (mean[i] !== null && Number.isFinite(mean[i])) idx.push(i);
    }
    return idx;
}

/**
 * TTD validado — Definição 1 do artigo (ACS Omega 2026, OBGS-FD/Auftek), aplicada
 * FIELMENTE sobre o sinal cru da réplica (sem média móvel, sem corte inicial):
 *   - baseline I0 = primeiros `baselineMinutes` (60) min; se <5 pontos, os primeiros
 *     min{n, max(30, ceil(0.1*n))} pontos;
 *   - B = MEDIANA de I0; MAD0 = mediana(|s - B|) em I0;
 *   - subindo (fluorescência): tau = max{B + madK*MAD0, floorFactor*B};
 *   - descendo (turbi/nefelo): tau espelhado = min{B - madK*MAD0, (2-floorFactor)*B};
 *   - i0 = 1o índice com `consecutive` (3) pontos consecutivos além de tau;
 *   - TTD = interpolação linear do cruzamento entre t_{i0-1} e t_{i0} (senão t_{i0});
 *     NA se não houver a tripla.
 * Defaults do artigo: baselineMinutes=60, madK=5, floorFactor=1.1, consecutive=3.
 *
 * @returns {{ ttd:number|null, threshold:number|null, baseline:number|null, direction:number }}
 */
export function detectTTD_JCSMA(signal, opts = {}) {
    const baselineMinutes = opts.baselineMinutes ?? 60;
    const madK = opts.madK ?? 5;
    const floorFactor = opts.floorFactor ?? 1.1;
    const consecutive = opts.consecutive ?? 3;
    const minSustainMinutes = opts.minSustainMinutes ?? 15;
    const n = Array.isArray(signal) ? signal.length : 0;
    if (n < consecutive + 1) {
        return { ttd: null, threshold: null, baseline: null, direction: 0 };
    }

    // Baseline I0 = primeiros `baselineMinutes` min do sinal; fallback do artigo se <5 pontos.
    const t0 = signal[0].tHours;
    const baseWinH = baselineMinutes / 60;
    let baseVals = signal
        .filter((p) => p.tHours - t0 <= baseWinH)
        .map((p) => p.value);
    if (baseVals.length < 5) {
        const k = Math.min(n, Math.max(30, Math.ceil(0.1 * n)));
        baseVals = signal.slice(0, k).map((p) => p.value);
    }
    const B = median(baseVals);
    if (B == null) return { ttd: null, threshold: null, baseline: null, direction: 0 };
    const mad0 = median(baseVals.map((v) => Math.abs(v - B))) ?? 0;

    // Direcao pela mediana da cauda (~ultimos 10%) vs baseline.
    const tailN = Math.max(1, Math.round(n * 0.1));
    const tailMed = median(signal.slice(-tailN).map((p) => p.value));
    const direction = tailMed != null && tailMed >= B ? 1 : -1;

    const tau =
        direction > 0
            ? Math.max(B + madK * mad0, floorFactor * B)
            : Math.min(B - madK * mad0, (2 - floorFactor) * B);

    const beyond = (v) => (direction > 0 ? v > tau : v < tau);

    // Sustentação robusta à CADÊNCIA. O artigo pede "3 pontos consecutivos" além de tau,
    // mas isso depende do intervalo de amostragem: na nossa aquisição de alta frequência,
    // 3 amostras (~2-3 min) é curto e um repique de ruído no baseline de um sensor barulhento
    // dispara TTD falso. Exigimos `consecutive` (3) E que o cruzamento persista por pelo menos
    // `minSustainMinutes` — traduzindo a intenção do artigo (cruzamento SUSTENTADO) para uma
    // duração, independente da cadência. Para crescimento real (sobe e fica), o TTD não muda.
    const dtH = medianDt(signal);
    const cadenceMin = dtH != null && dtH > 0 ? dtH * 60 : null;
    const needByTime = cadenceMin ? Math.ceil(minSustainMinutes / cadenceMin) : consecutive;
    const need = Math.max(consecutive, needByTime);

    // i0 = 1o indice com `need` pontos consecutivos alem de tau.
    let i0 = -1;
    for (let i = 0; i + need - 1 < n; i++) {
        let ok = true;
        for (let k = 0; k < need; k++) {
            if (!beyond(signal[i + k].value)) {
                ok = false;
                break;
            }
        }
        if (ok) {
            i0 = i;
            break;
        }
    }
    if (i0 < 0) {
        return { ttd: null, threshold: tau, baseline: B, direction, mad0, cadenceMin, need, n };
    }

    // TTD por interpolacao linear do cruzamento entre i0-1 e i0.
    let ttd = signal[i0].tHours;
    if (i0 > 0) {
        const p0 = signal[i0 - 1];
        const p1 = signal[i0];
        const crossed =
            direction > 0
                ? p0.value <= tau && p1.value > tau
                : p0.value >= tau && p1.value < tau;
        if (crossed && p1.value !== p0.value) {
            const frac = (tau - p0.value) / (p1.value - p0.value);
            ttd = p0.tHours + frac * (p1.tHours - p0.tHours);
        }
    }
    return { ttd, threshold: tau, baseline: B, direction, mad0, cadenceMin, need, n };
}

// ---------------------------------------------------------------------------
// 5. Fases lag / exponencial / estacionária (heurística pela 1ª derivada)
// ---------------------------------------------------------------------------

/**
 * Segmenta a curva média em lag / exponencial / estacionária a partir da
 * inclinação (na direção do crescimento). É um AUXÍLIO VISUAL ("estimado").
 *
 * @returns {{ lagEnd:number, expStart:number, expEnd:number, statStart:number }|null}
 */
export function detectPhases(grid, mean, opts = {}) {
    const idx = definedIdx(mean);
    if (idx.length < 4) return null;

    const baseline = mean[idx[0]];
    const endVal = mean[idx[idx.length - 1]];
    const direction = endVal - baseline >= 0 ? 1 : -1;

    // Magnitude de crescimento: cresce de ~0 (baseline) até o platô, esteja o
    // sinal subindo OU descendo com o cultivo.
    const g = (i) => direction * (mean[i] - baseline);

    // Platô = média da magnitude na cauda (últimos ~10% dos pontos) — robusto a ruído.
    const tailN = Math.max(3, Math.round(idx.length * 0.1));
    const tail = idx.slice(-tailN).map((i) => g(i)).filter(Number.isFinite);
    const gPlateau = tail.length
        ? tail.reduce((a, b) => a + b, 0) / tail.length
        : g(idx[idx.length - 1]);
    if (!(gPlateau > 0)) return null;

    // Início da subida (fim do lag) pela 1ª derivada — depois é ancorado no TTD.
    const slopes = [];
    for (let p = 1; p < idx.length; p++) {
        const i0 = idx[p - 1];
        const i1 = idx[p];
        const dt = grid[i1] - grid[i0];
        slopes.push({ i: i1, slope: dt > 0 ? (g(i1) - g(i0)) / dt : 0 });
    }
    const maxSlope = Math.max(...slopes.map((s) => s.slope), 0);
    const cut = (opts.slopeFraction ?? 0.2) * maxSlope;
    const rising = maxSlope > 0 ? slopes.filter((s) => s.slope >= cut) : [];
    const expStartIdx = rising.length ? rising[0].i : idx[0];

    // Início da ESTACIONÁRIA = quando a curva atinge `statFraction` do platô (achatou
    // de fato), e NÃO quando a inclinação cai abaixo de X% do pico — que começava
    // cedo demais, com a curva ainda descendo. Default 0.90 (90% do platô).
    const statFraction = opts.statFraction ?? 0.9;
    let statStartIdx = idx[idx.length - 1];
    for (const i of idx) {
        if (grid[i] >= grid[expStartIdx] && g(i) >= statFraction * gPlateau) {
            statStartIdx = i;
            break;
        }
    }

    return {
        lagEnd: grid[expStartIdx],
        expStart: grid[expStartIdx],
        expEnd: grid[statStartIdx],
        statStart: grid[statStartIdx],
    };
}

// ---------------------------------------------------------------------------
// 6. Conveniência: monta tudo que o gráfico precisa
// ---------------------------------------------------------------------------

/**
 * Pipeline completo: recebe ChartResponses por experimento, devolve o pacote
 * pronto para o GrowthCurveChart.
 *
 * @param {Array<{id:string, label:string, chart:object, gain?:number, tint?:number}>} items
 * @param {string} channel
 * @param {object} [opts]  – `mode` ('raw'|'basic'), + detectTTD (baselineN, k) e alignAndAggregate (gridStep)
 */
export function buildGrowthCurve(items, channel = DEFAULT_CHANNEL, opts = {}) {
    const mode = opts.mode === 'basic' ? 'basic' : 'raw';
    const replicas = [];
    const replicaMeta = [];
    (items || []).forEach((it, i) => {
        let convert;
        if (mode === 'basic') {
            // Sem ganho/tempo não dá pra normalizar: pula a réplica p/ não zerar a média.
            if (!it.gain || !it.tint) return;
            convert = (raw) => toBasicCount(raw, it.tint, it.gain);
        }
        const sig = toReplicaSignal(it.chart, channel, convert);
        if (sig.length >= 2) {
            replicas.push(sig);
            replicaMeta.push({
                id: it.id,
                label: it.label || `Réplica ${i + 1}`,
                points: sig.map((p) => [p.tHours, p.value]),
            });
        }
    });

    if (replicas.length === 0) {
        return { hasData: false, replicas: [], grid: [], mean: [], sd: [] };
    }

    const agg = alignAndAggregate(replicas, opts);

    // TTD pela Definição 1 do artigo (detectTTD_JCSMA), UM POR RÉPLICA (cada curva
    // tem o seu), sobre o sinal cru da réplica.
    const ttds = [];
    replicas.forEach((sig, i) => {
        const det = detectTTD_JCSMA(sig, opts);
        replicaMeta[i].baseline = det.baseline;
        replicaMeta[i].threshold = det.threshold;
        replicaMeta[i].ttd = det.ttd;
        if (det.ttd != null) ttds.push(det.ttd);
        if (opts.debug) {
            // eslint-disable-next-line no-console
            console.debug('[TTD]', replicaMeta[i].label, {
                n: det.n,
                cadenciaMin: det.cadenceMin,
                pontosSustentacao: det.need,
                B: det.baseline,
                mad0: det.mad0,
                tau: det.threshold,
                piso_1_1B: det.baseline != null ? 1.1 * det.baseline : null,
                ttd: det.ttd,
            });
        }
    });

    // TTD/limiar da própria curva MÉDIA — usados só para (a) ancorar as fases e
    // (b) desenhar UMA linha de limiar representativa. NÃO é "o" TTD exibido.
    const meanSignal = [];
    for (let i = 0; i < agg.grid.length; i++) {
        if (agg.mean[i] != null && Number.isFinite(agg.mean[i])) {
            meanSignal.push({ tHours: agg.grid[i], value: agg.mean[i] });
        }
    }
    const meanDet = detectTTD_JCSMA(meanSignal, opts);
    const meanTtd = meanDet.ttd ?? null;
    const threshold = meanDet.threshold ?? null;

    const phases = detectPhases(agg.grid, agg.mean, opts);
    // Fim da fase lag ≈ mediana dos TTD por réplica.
    const medianTtd = ttds.length ? median(ttds) : meanTtd;
    if (phases && medianTtd != null) {
        phases.lagEnd = medianTtd;
        if (phases.expStart < medianTtd) phases.expStart = medianTtd;
    }

    return {
        hasData: true,
        channel,
        grid: agg.grid,
        mean: agg.mean,
        sd: agg.sd,
        counts: agg.counts,
        replicas: replicaMeta, // cada uma agora tem { ttd, threshold, baseline }
        meanTtd,
        threshold,
        phases,
    };
}
